import {LocationInformation} from 'molbio/components/SaveLocationPicker';
import {TableSelection, TableSelectionType} from 'notebook/structured-tables/components/EntryTableDropdown';
import {WorklistFormType} from 'worklists/WorklistConstants';

/**
 * Given a LocationInformation and a display string for an entity potentially being saved, get error messages.
 * @param locationInfo Information about where to save entities and whether to add them to a worklist.
 * @param displayString The type of entity being saved, e.g. 'fragments', 'constructs', or 'primers'.
 */
export function getLocationInfoErrors({
  locationInfo,
  displayString,
}: {
  locationInfo: LocationInformation;
  displayString: 'constructs' | 'primers' | 'fragments';
}): Array<string> {
  const errors = [];
  const {targetFolderId, worklistParams} = locationInfo;
  if (targetFolderId == null) {
    errors.push(`A folder for ${displayString} is required.`);
  }
  if (worklistParams?.worklistFormType === WorklistFormType.NEW) {
    if (worklistParams?.newWorklistInfo?.name === '' || worklistParams.newWorklistInfo?.name == null) {
      errors.push(`A worklist name for ${displayString} is required.`);
    }
    if (worklistParams?.targetWorklistId != null) {
      errors.push(`Cannot add ${displayString} to both an existing worklist and a new worklist.`);
    }
  } else if (worklistParams?.worklistFormType === WorklistFormType.EXISTING) {
    if (worklistParams?.newWorklistInfo != null) {
      errors.push(`Cannot add ${displayString} to both an existing worklist and a new worklist.`);
    }
    if (worklistParams?.targetWorklistId == null) {
      errors.push(`A worklist for ${displayString} is required.`);
    }
  }
  return errors;
}

/**
 * Returns a validation error message if the table selection is incomplete, or null if it is valid.
 *
 * A selection is incomplete only when the user has started creating a new table but has not yet
 * entered a name (name is empty/whitespace). Selecting no table (null) is valid — the table
 * section is optional.
 */
export function getTableSelectionError(
  selectedTable: TableSelection,
  displayString: 'construct' | 'primer' | 'fragment'
): string | null {
  if (selectedTable?.type === TableSelectionType.NEW && !selectedTable.name.trim()) {
    return `A table name is required when creating a new ${displayString} table.`;
  }
  return null;
}
